import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { FC, useEffect, useState } from "react";
import { modalIgnoreDivergences } from "../../atoms/atom";
import { ComparationService } from "../../services/ComparationService";
import { Comparation } from "../../types/Comparation";
import { ComparationValues } from "../../types/ComparationValues";
import { useUtils } from "../../utils/utilFunctions";
import { useAlert } from "../contextProviders/AlertProvider";
import "./ModalIgnoreDivergences.scss";

interface Props {
	comparation: Comparation | undefined;
	onIgnored: () => void;
}

type DivergentField = {
	label: string;
	key: keyof ComparationValues;
	divergent: boolean;
};

const ModalIgnoreDivergences: FC<Props> = (props: Props) => {
	const [open, setOpen] = useAtom(modalIgnoreDivergences);
	const [selectedFields, setSelectedFields] = useState<string[]>([]);
	const [saving, setSaving] = useState(false);
	const { showAlert, showError } = useAlert();
	const { formatIsoDate, formatCbo } = useUtils();
	const comparationService = new ComparationService();

	const divergentFields = () => {
		const comparation = props.comparation;
		if (!comparation) {
			return [];
		}
		const allFields: DivergentField[] = [
			{ label: 'Nome', key: 'name', divergent: comparation.divergentNome },
			{ label: 'Cargo', key: 'position', divergent: comparation.divergentCargo },
			{ label: 'CPF', key: 'cpf', divergent: comparation.divergentCpf },
			{ label: 'Data de admissão', key: 'admissionDate', divergent: comparation.divergentDataAdmissao },
			{ label: 'Data de desligamento', key: 'dismissalDate', divergent: comparation.divergentDataDesligamento },
			{ label: 'Data de nascimento', key: 'birthDate', divergent: comparation.divergentDataNascimento },
			{ label: 'CBO', key: 'cbo', divergent: comparation.divergentDescricaoCbo },
			{ label: 'Local de trabalho', key: 'workplace', divergent: comparation.divergentLocalTrabalho },
			{ label: 'Matrícula', key: 'registration', divergent: comparation.divergentMatricula },
			{ label: 'Situação', key: 'situation', divergent: comparation.divergentSituacao }
		];
		return allFields.filter(field => field.divergent);
	};

	const formatValue = (key: string, value: string | undefined) => key === 'cbo'
		? formatCbo(value || '-')
		: formatIsoDate(value || '-');

	const toggleField = (key: string) => {
		setSelectedFields(prev => prev.includes(key)
			? prev.filter(item => item !== key)
			: [...prev, key]
		);
	};

	const ignore = async () => {
		if (!props.comparation) {
			return;
		}
		setSaving(true);
		const response = await comparationService.ignore(props.comparation.id, { fields: selectedFields });
		setSaving(false);
		if (!response || response.status !== 200) {
			showError(response?.data?.error);
			return;
		}
		showAlert('Divergências ignoradas com sucesso!');
		setOpen(false);
		props.onIgnored();
	};

	useEffect(() => {
		setSelectedFields([]);
	}, [props.comparation?.id, open]);

	const fields = divergentFields();

	return (
		<Dialog
			open={open}
			onClose={() => setOpen(false)}
			className="modal-ignore-divergences"
			fullWidth
			maxWidth="md"
		>
			<DialogTitle>Ignorar divergências de {props.comparation?.name}</DialogTitle>
			<DialogContent>
				{!!fields.length && <Alert severity="warning" variant="outlined" className="info-message">
					Selecione as divergências que devem ser ignoradas nas próximas execuções.
					Enquanto os valores do SGG e do eSocial forem exatamente os exibidos abaixo,
					o campo não será mais apontado como divergente para as empresas desta execução.
				</Alert>}
				{!fields.length
					? <Typography>Nenhuma divergência de campo para exibir.</Typography>
					: <TableContainer component={Paper}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Campo</TableCell>
									<TableCell>SGG</TableCell>
									<TableCell>eSocial</TableCell>
									<TableCell align="center">Ignorar</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{fields.map(field => (
									<TableRow key={field.key}>
										<TableCell className="field-label">{field.label}</TableCell>
										<TableCell>{formatValue(field.key, props.comparation?.sggValues?.[field.key])}</TableCell>
										<TableCell>{formatValue(field.key, props.comparation?.esocialValues?.[field.key])}</TableCell>
										<TableCell align="center">
											<Switch
												checked={selectedFields.includes(field.key)}
												onChange={() => toggleField(field.key)}
											/>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				}
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setOpen(false)}>Fechar</Button>
				{!!selectedFields.length && <Button
					variant="contained"
					color="warning"
					onClick={ignore}
					disabled={saving}
				>
					{saving ? <CircularProgress size="3vh"/> : 'Ignorar'}
				</Button>}
			</DialogActions>
		</Dialog>
	);
}

export default ModalIgnoreDivergences;